import { z } from 'zod';
import { idSchema, localDateSchema, markdownSchema, nameSchema, rateSchema, urlSchema, versionSchema } from './common';

/**
 * Projekty (docs/3VTOS_MVP.md §5, docs/DATA_MODEL.md „crm”): `projects`, `project_members`
 * (packages/db/src/schema/crm.ts). Moduł uprawnień: `projects`.
 *
 * Projekt: klient opcjonalny; projekt wewnętrzny (`isInternal`) nie ma klienta (CHECK w bazie).
 * Członkowie projektu wyznaczają zakres „Tylko przypisani”. Wejścia i wyjścia usług dopisuje agent modułu.
 */

export const PROJECT_STATUSES = ['planned', 'active', 'on_hold', 'done', 'cancelled'] as const;
export type ProjectStatus = (typeof PROJECT_STATUSES)[number];
export const projectStatusSchema = z.enum(PROJECT_STATUSES);

/** Rola w projekcie: prowadzący (opiekun projektu w listach) albo członek. */
export const PROJECT_MEMBER_ROLES = ['lead', 'member'] as const;
export type ProjectMemberRole = (typeof PROJECT_MEMBER_ROLES)[number];
export const projectMemberRoleSchema = z.enum(PROJECT_MEMBER_ROLES);

/** Budżet godzin w sekundach (np. 40 h = 144 000 s). */
export const hoursBudgetSecondsSchema = z
  .number()
  .int()
  .positive()
  .max(100_000 * 3600);

const projectFieldsBase = z.object({
  clientId: idSchema.nullable().optional(),
  name: nameSchema,
  goal: z.string().trim().max(2000).nullable().optional(),
  scopeMd: markdownSchema.nullable().optional(),
  status: projectStatusSchema.default('active'),
  startDate: localDateSchema.nullable().optional(),
  endDate: localDateSchema.nullable().optional(),
  repoUrl: urlSchema.nullable().optional(),
  figmaUrl: urlSchema.nullable().optional(),
  /** projekt wewnętrzny (bez klienta), np. „Fravity OS” */
  isInternal: z.boolean().default(false),
  /** domyślna rozliczalność wpisów czasu w projekcie */
  billableDefault: z.boolean().default(true),
  hoursBudgetSeconds: hoursBudgetSecondsSchema.nullable().optional(),
});

export type ProjectFields = z.infer<typeof projectFieldsBase>;

/**
 * Reguły spójności pól projektu (te same pilnuje baza: CHECK). Dla wejść częściowych (update)
 * sprawdzaj je na scalonym rekordzie: `projectFieldsRefinement({ ...row, ...patch }, ctx)`.
 */
export function projectFieldsRefinement(
  v: Partial<Pick<ProjectFields, 'isInternal' | 'clientId' | 'startDate' | 'endDate'>>,
  ctx: z.RefinementCtx,
): void {
  if (v.isInternal && v.clientId) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['clientId'], message: 'Projekt wewnętrzny nie ma klienta' });
  }
  if (v.startDate && v.endDate && v.endDate < v.startDate) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['endDate'], message: 'Koniec przed początkiem' });
  }
}

/** Pola projektu bez reguł spójności (do `.extend()`, `.partial()`, `.pick()`). */
export const projectFieldsShape = projectFieldsBase;
/** Pola projektu z regułami spójności. */
export const projectFieldsSchema = projectFieldsBase.superRefine(projectFieldsRefinement);

export const projectMemberFieldsSchema = z.object({
  projectId: idSchema,
  userId: idSchema,
  role: projectMemberRoleSchema.default('member'),
});
export type ProjectMemberFields = z.infer<typeof projectMemberFieldsSchema>;

// ── Usługi modułu Projekty (packages/core/src/modules/projects) ─────────────
// Wejścia usług (router tRPC `projects`) i kształty DTO. Projekty nie mają pól pieniężnych, więc DTO nie zależą
// od flagi „Widzi stawki i kwoty”.

/** Statusy „otwarte” (zakładka Aktywne) i „zamknięte” (zakładka Zakończone). */
export const OPEN_PROJECT_STATUSES = ['planned', 'active', 'on_hold'] as const satisfies readonly ProjectStatus[];
export const CLOSED_PROJECT_STATUSES = ['done', 'cancelled'] as const satisfies readonly ProjectStatus[];

/**
 * Widoki listy (zakładki z Figmy 07 Projekty):
 *  - `active`: projekty otwarte (planowany, aktywny, wstrzymany), bez archiwum,
 *  - `internal`: projekty wewnętrzne (bez klienta), bez archiwum,
 *  - `closed`: zakończone i anulowane oraz zarchiwizowane,
 *  - `all`: bez ograniczenia widoku (pozostałe filtry działają).
 */
export const PROJECT_LIST_VIEWS = ['active', 'internal', 'closed', 'all'] as const;
export type ProjectListView = (typeof PROJECT_LIST_VIEWS)[number];

/** Archiwum w liście: pomiń (domyślnie), dołącz albo tylko archiwum. */
export const ARCHIVED_FILTERS = ['exclude', 'include', 'only'] as const;
export type ArchivedFilter = (typeof ARCHIVED_FILTERS)[number];

export const listProjectsInput = z
  .object({
    view: z.enum(PROJECT_LIST_VIEWS).default('all'),
    clientId: idSchema.optional(),
    status: z.array(projectStatusSchema).min(1).max(PROJECT_STATUSES.length).optional(),
    /** true = tylko wewnętrzne, false = bez wewnętrznych */
    internal: z.boolean().optional(),
    /** domyślnie `exclude`; w widoku `closed` domyślnie `include` */
    archived: z.enum(ARCHIVED_FILTERS).optional(),
    /** prowadzący (rola `lead` w project_members): filtr „Opiekun” */
    leadUserId: idSchema.optional(),
    /** dowolny członek projektu */
    memberUserId: idSchema.optional(),
    /** wyszukiwanie po nazwie (bez polskich znaków) */
    q: z.string().trim().max(200).optional(),
    limit: z.number().int().min(1).max(500).default(500),
  })
  .strict()
  .default({});
export type ListProjectsInput = z.input<typeof listProjectsInput>;

export const getProjectInput = z.object({ id: idSchema }).strict();

/** Projekty klienta (profil klienta: „Projekty i usługi”). Osoba w trybie częściowym widzi tylko swoje projekty. */
export const listClientProjectsInput = z
  .object({
    clientId: idSchema,
    /** dołącz zakończone i anulowane (domyślnie tylko otwarte) */
    includeClosed: z.boolean().default(false),
    includeArchived: z.boolean().default(false),
  })
  .strict();

/** Wybór projektu w innych modułach (zadania, czas): otwarte projekty z zakresu osoby, opcjonalnie jednego klienta. */
export const projectOptionsInput = z
  .object({
    clientId: idSchema.optional(),
    /** dołącz zakończone i anulowane (np. edycja starego wpisu) */
    includeClosed: z.boolean().default(false),
  })
  .strict()
  .default({});

/** Klienci, dla których osoba może założyć projekt (pełny dostęp z edycją, bez archiwum). */
export const projectClientOptionsInput = z.object({}).strict().optional();

/**
 * Nowy projekt. Projekt wewnętrzny (`isInternal`) nie ma klienta i domyślnie jest nierozliczany
 * (`billableDefault` pominięte = `!isInternal`). `leadUserId` pominięte = autor prowadzi projekt.
 */
export const createProjectInput = projectFieldsShape
  .extend({
    billableDefault: z.boolean().optional(),
    leadUserId: idSchema.nullable().optional(),
  })
  .strict()
  .superRefine(projectFieldsRefinement);
export type CreateProjectInput = z.input<typeof createProjectInput>;

/**
 * Edycja (wersja rekordu: CONFLICT przy niezgodności). Pola pominięte zostają bez zmian. Zmiana klienta projektu,
 * który ma zadania lub zasoby innego klienta, kończy się CONFLICT (bez cichego przepinania).
 * `leadUserId`: nowy prowadzący (poprzedni zostaje członkiem); `null` = bez prowadzącego.
 */
export const updateProjectInput = projectFieldsShape
  .partial()
  .extend({
    id: idSchema,
    version: versionSchema,
    leadUserId: idSchema.nullable().optional(),
  })
  .strict();
export type UpdateProjectInput = z.input<typeof updateProjectInput>;

/** Archiwizacja i przywrócenie (odwracalne). `version` opcjonalnie: CONFLICT przy niezgodności. */
export const archiveProjectInput = z.object({ id: idSchema, version: versionSchema.optional() }).strict();

export const listProjectMembersInput = z.object({ projectId: idSchema }).strict();
export const addProjectMemberInput = projectMemberFieldsSchema.strict();
export type AddProjectMemberInput = z.input<typeof addProjectMemberInput>;
export const removeProjectMemberInput = z.object({ projectId: idSchema, userId: idSchema }).strict();
/**
 * Stawka partnera osoby w projekcie (DECISIONS 2026-10-08 [P] „Stawka partnera”): owner / admin / manager (manager nie
 * zmienia własnej). `null` = domyślna z członkostwa. Nierozliczone wpisy osoby w projekcie dostają nową stawkę.
 */
export const setProjectMemberPartnerRateInput = z
  .object({ projectId: idSchema, userId: idSchema, partnerRate: rateSchema.nullable() })
  .strict();

/** Ustawienia rozliczeniowe projektu: zmienia osoba z edycją całego klienta (albo owner / admin / manager). */
export const PROJECT_BILLING_FIELDS = ['billableDefault', 'hoursBudgetSeconds'] as const;
export const PROJECT_MESSAGES = {
  billingForbidden: 'Ustawienia rozliczeń projektu zmienia osoba z dostępem do całego klienta',
} as const;

// ── DTO ─────────────────────────────────────────────────────────────────────
/** Osoba w skrócie (avatar, imię). */
export interface PersonRef {
  userId: string;
  name: string;
  initials: string;
  avatarColor: string;
}

/** Klient projektu w skrócie (kolor = dane klienta, kropka w listach). */
export interface ProjectClientRef {
  id: string;
  name: string;
  color: string | null;
}

/** Wiersz listy projektów (07 Projekty, profil klienta). */
export interface ProjectListItem {
  id: string;
  version: number;
  name: string;
  clientId: string | null;
  client: ProjectClientRef | null;
  status: ProjectStatus;
  isInternal: boolean;
  billableDefault: boolean;
  startDate: string | null;
  endDate: string | null;
  /** budżet godzin w sekundach (null = bez budżetu) */
  hoursBudgetSeconds: number | null;
  /**
   * Wykorzystany czas w sekundach. Fala 1: 0 (brak wpisów czasu); moduł Czas (Fala 2) podłącza
   * `registerProjectUsageResolver` w `packages/core/src/modules/projects/usage.ts`.
   */
  usedSeconds: number;
  /** prowadzący (rola `lead`); przy kilku: najwcześniej dodany */
  lead: PersonRef | null;
  memberCount: number;
  archivedAt: Date | null;
  updatedAt: Date;
}

export interface ProjectMember extends PersonRef {
  /** id wiersza project_members */
  id: string;
  email: string;
  role: ProjectMemberRole;
  /** członkostwo w workspace wyłączone (osoba nie ma dostępu, historia zostaje) */
  disabled: boolean;
  addedAt: Date;
  /**
   * stawka partnera w projekcie (PLN/h; null = domyślna) i domyślna z członkostwa — tylko dla owner / admin / manager
   * i dla samej osoby (pominięte dla innych)
   */
  partnerRate?: string | null;
  defaultPartnerRate?: string | null;
}

/** Szczegóły projektu (/projekty/:id). */
export interface ProjectDetail extends ProjectListItem {
  goal: string | null;
  scopeMd: string | null;
  repoUrl: string | null;
  figmaUrl: string | null;
  createdAt: Date;
  createdBy: string | null;
  members: ProjectMember[];
  /** co osoba może zrobić (UI ukrywa akcje; backend i tak sprawdza) */
  can: {
    edit: boolean;
    manageMembers: boolean;
    /** „do rozliczenia” domyślnie i budżet godzin (edycja całego klienta albo owner / admin / manager) */
    editBilling: boolean;
    /** widzi i ustawia stawki partnera członków (owner / admin / manager) */
    partnerRates: boolean;
  };
}

export type ProjectClientOption = ProjectClientRef;
